import { computed, ref, shallowRef, type Ref } from 'vue'
import { type EntryKey, useQuery, useQueryCache } from '@pinia/colada'
import { useAuthStore } from '@/stores/authStore'

// ─── Shared types ──────────────────────────────────────────────────────────────

export type PrerequisiteDescriptor = {
  type: 'LINE_ITEM' | 'DEBTOR'
  lytsoftId: string
  deepLink: string
}

export type LinkResponse<T> = {
  row: T | null
  alreadyAssociated: boolean
  prerequisites?: PrerequisiteDescriptor[]
  error?: string
}

/**
 * Association-state filter for the QBO tab (Phase 28 D-28-05..08).
 *
 * <p>Values:
 * <ul>
 *   <li>{@code 'all'} — no filter applied ({@code linked: undefined})</li>
 *   <li>{@code 'linked'} — only associated QBO rows ({@code linked: true})</li>
 *   <li>{@code 'unlinked'} — only unassociated QBO rows ({@code linked: false})</li>
 *   <li>{@code 'syncing'} — treated as no filter ({@code linked: undefined}).
 *       "Syncing" is a Lytning queue concept with no QBO equivalent; the shared
 *       filter row includes the button for UI consistency, but the composable
 *       does not apply a backend filter for it.</li>
 * </ul>
 */
export type AssociationFilter = 'all' | 'linked' | 'unlinked' | 'syncing'

/**
 * Optional context threaded into the {@link EntityLinkStudioOptions.searchPredicate}
 * invocation. Phase 28 adds {@code linked} so the predicate (the API client
 * function) can append {@code &linked=true|false} to the request URL.
 */
export type SearchPredicateOptions = { linked?: boolean }

export type EntityLinkStudioOptions<T> = {
  searchPredicate: (
    query: string,
    page: number,
    size: number,
    options?: SearchPredicateOptions,
  ) => Promise<{
    items: T[]
    totalCount: number
    page: number
    size: number
  }>
  // linkAction / reverseLookupAction return unknown — the composable narrows the
  // result at runtime using shared field names (alreadyAssociated, error,
  // prerequisites). Consumers pass functions returning LinkResponse<T>, which
  // satisfies those fields structurally.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  linkAction: (qboId: string, lytId: string) => Promise<unknown>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reverseLookupAction?: (qboId?: string, lytId?: string) => Promise<unknown>
  entityLabel: string
}

export type EntityLinkStudioReturn<T> = {
  // State refs
  query: Ref<string>
  page: Ref<number>
  size: Ref<number>
  results: Ref<T[]>
  totalCount: Ref<number>
  loading: Ref<boolean>
  error: Ref<string | null>
  linkResult: Ref<LinkResponse<T> | null>
  selectedTab: Ref<'lytning' | 'qbo'>
  // Phase 28 — association-state filter on the QBO tab
  associationFilter: Ref<AssociationFilter>
  // Handlers
  searchQbo: (query?: string, page?: number) => Promise<void>
  reverseLookup: (qboId?: string, lytId?: string) => Promise<void>
  linkEntities: (lytId: string, qboId: string) => Promise<void>
  clear: () => void
  nextPage: () => Promise<void>
  prevPage: () => Promise<void>
  setSize: (n: number) => void
  setAssociationFilter: (value: AssociationFilter) => void
}

// ─── Composable ───────────────────────────────────────────────────────────────

type SearchPageResponse<T> = {
  items: T[]
  totalCount: number
  page: number
  size: number
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function loadPageSize(entityLabel: string): number {
  try {
    const stored = window?.localStorage?.getItem(`entityStudio.${entityLabel}.pageSize`)
    if (stored !== null && stored !== undefined) {
      return clamp(Number(stored), 1, 50)
    }
  } catch {
    // localStorage unavailable (Node env / SSR)
  }
  return 20
}

function savePageSize(entityLabel: string, size: number): void {
  try {
    window?.localStorage?.setItem(`entityStudio.${entityLabel}.pageSize`, String(size))
  } catch {
    // localStorage unavailable
  }
}


export function useEntityLinkStudio<T>(options: EntityLinkStudioOptions<T>): EntityLinkStudioReturn<T> {
  const { searchPredicate, linkAction, reverseLookupAction, entityLabel } = options
  const queryCache = useQueryCache()
  const auth = useAuthStore()

  const query = ref<string>('')
  const page = ref<number>(1)
  const size = ref<number>(loadPageSize(entityLabel))
  const linkResult = shallowRef<LinkResponse<T> | null>(null)
  const selectedTab = ref<'lytning' | 'qbo'>('qbo')
  // Phase 28 — default to 'all' so existing users see no change on first load.
  const associationFilter = ref<AssociationFilter>('all')

  // Gate: no network until the user (or filter/page helpers) explicitly searches.
  // setAssociationFilter / nextPage / setSize all flip this via searchQbo.
  const searchEnabled = ref(false)

  const linked = computed(() => {
    const filter = associationFilter.value
    if (filter === 'linked') return true
    if (filter === 'unlinked') return false
    return undefined
  })

  // Prefer selectedStore.storeId (store-login flow); fall back to legacy storeID.
  // Identity dimensions are non-secret only — never apiKey or tokens.
  const packageId = computed(() => auth.selectedPackageId ?? null)
  const integrationId = computed(() => auth.currentIntegration?.integrationId ?? null)
  const storeId = computed(
    () => auth.selectedStore?.storeId ?? (auth.storeID || null),
  )

  // Cache identity must include every input that changes the predicate args
  // plus auth context so QBO results never leak across package/store/integration.
  const searchKey = computed<EntryKey>(() => [
    'entity-link-studio',
    entityLabel,
    packageId.value,
    integrationId.value,
    storeId.value,
    query.value,
    page.value,
    size.value,
    linked.value ?? null,
  ])

  const {
    data: searchData,
    error: searchError,
    isLoading,
    refetch,
  } = useQuery<SearchPageResponse<T>>({
    key: () => searchKey.value,
    // enabled becomes true only after an explicit search; key changes then auto-refetch.
    enabled: () => searchEnabled.value,
    // Studio searches are user-driven; avoid background refetches mutating the grid.
    staleTime: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    query: async () =>
      searchPredicate(query.value, page.value, size.value, { linked: linked.value }),
  })

  // Public results stay empty until success; on query error match prior contract (clear rows).
  const results = computed<T[]>(() =>
    searchError.value ? [] : (searchData.value?.items ?? []),
  )
  const totalCount = computed<number>(() => searchData.value?.totalCount ?? 0)

  // Bind public loading to Colada isLoading (not isPending).
  const loading = computed(() => isLoading.value)

  // Write-path errors share the public error channel with search failures.
  const actionError = ref<string | null>(null)

  // Preserve string|null public error contract (Colada stores Error | null).
  const error = computed<string | null>(() => {
    if (actionError.value) return actionError.value
    const err = searchError.value
    if (!err) return null
    return err instanceof Error ? err.message : String(err)
  })

  // Normalize page/size from the server response without breaking the public refs.
  // Only apply when data is present and differs so we do not thrash the cache key.
  function applyPageNormalization(response: SearchPageResponse<T> | undefined): void {
    if (!response) return
    if (response.page && response.page !== page.value) {
      page.value = response.page
    }
    if (response.size && response.size !== size.value) {
      size.value = response.size
    }
  }

  async function searchQbo(searchQuery?: string, searchPage?: number): Promise<void> {
    if (searchQuery !== undefined) query.value = searchQuery
    if (searchPage !== undefined) page.value = searchPage

    actionError.value = null
    searchEnabled.value = true

    // refetch waits for the in-flight (or newly keyed) query to settle.
    const state = await refetch()
    applyPageNormalization(state.data ?? searchData.value)
  }

  async function reverseLookup(qboId?: string, lytId?: string): Promise<void> {
    if (!reverseLookupAction) {
      console.warn(`[useEntityLinkStudio] reverseLookupAction not provided for entity "${entityLabel}"`)
      return
    }

    linkResult.value = null

    try {
      const response = await reverseLookupAction(qboId, lytId)
      linkResult.value = response as LinkResponse<T>
    } catch (err) {
      actionError.value = err instanceof Error ? err.message : 'Reverse lookup failed'
    }
  }

  async function linkEntities(lytId: string, qboId: string): Promise<void> {
    linkResult.value = null
    actionError.value = null

    try {
      const raw = (await linkAction(qboId, lytId)) as {
        alreadyAssociated: boolean
        error?: string
        prerequisites?: PrerequisiteDescriptor[]
        row?: T | null
      }

      // Normalize 422 (prerequisites) and 409 (conflict) based on response shape
      if (raw.prerequisites && raw.prerequisites.length > 0) {
        linkResult.value = {
          row: null,
          alreadyAssociated: false,
          prerequisites: raw.prerequisites,
          error: raw.error ?? 'UNRESOLVED_PREREQUISITES',
        }
      } else if (raw.error === 'CONFLICT') {
        linkResult.value = {
          row: raw.row ?? null,
          alreadyAssociated: true,
          error: 'CONFLICT',
        }
      } else {
        linkResult.value = raw as LinkResponse<T>

        // Successful link can change which rows match linked/unlinked filters —
        // refresh any active search for this entity label only.
        if (!raw.error && !raw.alreadyAssociated && searchEnabled.value) {
          // Mark entity search entries stale, then force the current key to reload
          // so the open QBO grid reflects the new association state.
          await queryCache.invalidateQueries({
            key: ['entity-link-studio', entityLabel],
            exact: false,
          })
          await refetch()
        }
      }
    } catch (err) {
      actionError.value = err instanceof Error ? err.message : 'Link failed'
    }
  }

  function clear(): void {
    query.value = ''
    page.value = 1
    // Disable so clear does not trigger a fresh empty search; drop cached pages
    // so results/totalCount/error compute back to empty/null.
    searchEnabled.value = false
    for (const entry of queryCache.getEntries({
      key: ['entity-link-studio', entityLabel],
      exact: false,
    })) {
      queryCache.remove(entry)
    }
    linkResult.value = null
    actionError.value = null
    // Phase 28 D-28-14 — reset to 'all' on clear() so subsequent searches
    // start unfiltered.
    associationFilter.value = 'all'
  }

  function setAssociationFilter(value: AssociationFilter): void {
    associationFilter.value = value
    // Reset to page 1 on filter change (avoid landing on an empty page when
    // the user's current page is past the filtered result count).
    page.value = 1
    void searchQbo(query.value, 1)
  }

  async function nextPage(): Promise<void> {
    page.value = page.value + 1
    await searchQbo(query.value, page.value)
  }

  async function prevPage(): Promise<void> {
    const prev = page.value - 1
    if (prev < 1) return
    page.value = prev
    await searchQbo(query.value, page.value)
  }

  function setSize(n: number): void {
    const clamped = clamp(n, 1, 50)
    size.value = clamped
    savePageSize(entityLabel, clamped)
    page.value = 1
    void searchQbo(query.value, page.value)
  }

  return {
    query,
    page,
    size,
    results,
    totalCount,
    loading,
    error,
    linkResult,
    selectedTab,
    associationFilter,
    setAssociationFilter,
    searchQbo,
    reverseLookup,
    linkEntities,
    clear,
    nextPage,
    prevPage,
    setSize,
  }
}
